import { useEffect, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useDebounce } from "@unreserved/hooks/use-debounce";
import {
  type DocumentRequestFormData,
  documentRequestSchema,
} from "@unreserved/schemas/enquiries/document-request-schema";
import type { PropertyListItemDto } from "@unreserved/sdk";
import { Checkbox } from "@unreserved/ui/components/checkbox";
import { Input } from "@unreserved/ui/components/input";
import { Label } from "@unreserved/ui/components/label";
import { Controller, useForm } from "react-hook-form";

import { useEnquiryModalDropdown } from "@/app/_components/my-enquiries/modal/enquiry-modal-dropdown-context";
import { PropertyAddressInput } from "@/app/_components/my-enquiries/modal/property-address-input";
import {
  useCreateDocumentRequestEnquiry,
  usePropertiesForEnquiry,
} from "@/hooks/my-enquiry/use-my-enquiry";
import { useAuthStore } from "@/stores/auth-store";

export const DocumentRequestForm = () => {
  const user = useAuthStore((s) => s.user);
  const userEmail = user?.email != null ? String(user.email) : "";
  const [selectedProperty, setSelectedProperty] = useState<PropertyListItemDto | null>(null);
  const [isPropertyDropdownOpen, setIsPropertyDropdownOpen] = useState(false);
  const [propertySearchText, setPropertySearchText] = useState("");
  const debouncedSearch = useDebounce(propertySearchText, 300);

  const {
    data: propertiesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = usePropertiesForEnquiry({
    propertyTitle: debouncedSearch || undefined,
  });

  const properties = useMemo(
    () => propertiesData?.pages?.flatMap((page) => page.data?.properties ?? []) ?? [],
    [propertiesData],
  );

  const propertySuggestions = useMemo(
    () =>
      properties.map((p) => ({
        id: p.id,
        displayLabel: p.title != null && typeof p.title === "string" ? p.title : p.id,
        data: p as PropertyListItemDto,
      })),
    [properties],
  );

  const createDocumentRequest = useCreateDocumentRequestEnquiry();
  const { setIsSubmitting, setSuccessConfig, preselectedListingId } = useEnquiryModalDropdown();

  useEffect(() => {
    setIsSubmitting(createDocumentRequest.isPending);
  }, [createDocumentRequest.isPending, setIsSubmitting]);

  const {
    register: registerField,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<DocumentRequestFormData>({
    resolver: zodResolver(documentRequestSchema),
    mode: "onSubmit",
    defaultValues: {
      email: "",
      propertyAddress: "",
      documents: [],
      subjectToFinance: false,
      subjectToInspection: false,
      subjectToSpecialConditions: false,
      interestedInBuildingAndPestInspection: false,
    },
  });

  useEffect(() => {
    if (userEmail) setValue("email", userEmail);
  }, [userEmail, setValue]);

  useEffect(() => {
    if (preselectedListingId && properties.length > 0 && !selectedProperty) {
      const match = properties.find(
        (p) => p.id === preselectedListingId || String(p.listingId) === preselectedListingId,
      );
      if (match) {
        setSelectedProperty(match);
        setValue("propertyAddress", typeof match.title === "string" ? match.title : match.id);
      }
    }
  }, [preselectedListingId, properties, selectedProperty, setValue]);

  const onSubmit = (data: DocumentRequestFormData) => {
    if (!selectedProperty?.listingId) return;
    const listingId =
      typeof selectedProperty.listingId === "string"
        ? selectedProperty.listingId
        : String(selectedProperty.listingId);
    createDocumentRequest.mutate(
      {
        email: data.email,
        listingId,
        propertyMediaIds: data.documents,
        isBAndPInspectionRequired: data.interestedInBuildingAndPestInspection,
      },
      {
        onSuccess: () => {
          setSuccessConfig({
            title: "Document Request Submitted !!",
            subheading: "Team will review and get back , you'll be notified once responded",
            primaryButtonLabel: "Back to Home",
            primaryButtonRedirect: "dashboard",
            extraButton: true,
            extraButtonLabel: "Back",
          });
        },
      },
    );
  };

  return (
    <form id="document-request-form" onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-5">
        {/* Email */}
        <div className="space-y-1.5">
          <Label className="text-typography-750 text-sm font-medium">
            Email ID
            {/* <span className="text-error-500">*</span> */}
          </Label>
          <Input
            type="email"
            placeholder="Enter here"
            className="border-border-400 text-typography-900 placeholder:text-typography-400 focus-visible:border-primary-500 focus-visible:ring-primary-200 h-9 w-full rounded border !bg-white px-3 text-sm focus-visible:ring-4 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-70"
            {...registerField("email")}
            aria-invalid={errors.email ? "true" : "false"}
            disabled={!!userEmail}
          />
          {errors.email && <p className="text-error-600 text-sm">{errors.email.message}</p>}
        </div>
        {/* Property */}
        <div className={`space-y-1.5 ${isPropertyDropdownOpen ? "mb-40" : ""}`}>
          <Label className="text-typography-750 text-sm font-medium">Select Property</Label>
          <Controller
            control={control}
            name="propertyAddress"
            render={({ field }) => {
              const selectedDisplayLabel =
                selectedProperty != null
                  ? typeof selectedProperty.title === "string"
                    ? selectedProperty.title
                    : selectedProperty.id
                  : "";
              return (
                <PropertyAddressInput<PropertyListItemDto>
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    setPropertySearchText(value);
                    if (value === "" || value !== selectedDisplayLabel) {
                      setSelectedProperty(null);
                      setValue("documents", []);
                    }
                  }}
                  placeholder="Select property"
                  externalSuggestions={propertySuggestions}
                  onSelectSuggestion={(item) => {
                    if (item.data) {
                      setSelectedProperty(item.data);
                      field.onChange(item.displayLabel);
                      setPropertySearchText("");
                      setValue("documents", []);
                    }
                  }}
                  onDropdownOpenChange={(open) => {
                    setIsPropertyDropdownOpen(open);
                  }}
                  onEndReached={() => {
                    if (hasNextPage && !isFetchingNextPage) {
                      void fetchNextPage();
                    }
                  }}
                  loadingMore={isFetchingNextPage}
                  hasMore={hasNextPage}
                  loading={isLoading}
                />
              );
            }}
          />
          {errors.propertyAddress && (
            <p className="text-error-600 text-sm">{errors.propertyAddress.message}</p>
          )}
        </div>

        {/* Documents (from selected property) */}
        <div className="space-y-1.5">
          <h2 className="text-typography-750 text-sm font-medium">
            <span>Select all documents you want to access</span>
          </h2>
          {selectedProperty ? (
            selectedProperty.documents.length > 0 ? (
              <Controller
                control={control}
                name="documents"
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    {selectedProperty.documents.map((doc) => {
                      const docId = doc.id;
                      const displayLabel =
                        typeof doc.fileName === "string" ? doc.fileName : "Document";
                      const isChecked = field.value.includes(docId);
                      return (
                        <Label
                          key={docId}
                          className="text-typography-900 flex cursor-pointer items-center gap-2">
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                field.onChange([...field.value, docId]);
                              } else {
                                field.onChange(field.value.filter((id) => id !== docId));
                              }
                            }}
                            aria-label={displayLabel}
                            className="data-[state=checked]:!bg-primary-700 data-[state=checked]:border-primary-700 !bg-white data-[state=checked]:text-white"
                          />
                          <span className="text-sm">{displayLabel}</span>
                        </Label>
                      );
                    })}
                  </div>
                )}
              />
            ) : (
              <p className="text-typography-500 text-sm">
                No documents available for this property.
              </p>
            )
          ) : (
            <p className="text-typography-500 text-sm">
              Select a property to see available documents.
            </p>
          )}
          {errors.documents && <p className="text-error-600 text-sm">{errors.documents.message}</p>}
        </div>
        <Controller
          control={control}
          name="interestedInBuildingAndPestInspection"
          render={({ field }) => (
            <Label className="text-typography-900 flex cursor-pointer items-center gap-2">
              <span className="text-sm">Are you interested in building & pest inspection?</span>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
                aria-label="Agree to Terms and Conditions"
                className="data-[state=checked]:!bg-primary-700 data-[state=checked]:border-primary-700 !bg-white data-[state=checked]:text-white"
              />
            </Label>
          )}
        />
        {errors.interestedInBuildingAndPestInspection && (
          <p className="text-error-600 text-sm">
            {errors.interestedInBuildingAndPestInspection.message}
          </p>
        )}
      </div>
    </form>
  );
};
